import * as logger from "common/logger";
import { getDefaultAggregation } from "components/reportsV3/utils/aggregationUtils";
import { inputColumnView } from "components/reportsV3/utils/inputColumnUtils";
import {
  joinDisplayNameOf,
  joinsFormSingleResult,
} from "components/reportsV3/utils/joinUtils";
import { isGroupingColumn } from "components/reportsV3/utils/rangeGroupingUtils";

// Assigns unique column names — the second occurrence of "foo" becomes
// "foo_2", the third "foo_3" — fixing sourceColumn to the original internal
// name so the back end can still resolve the field. Duplicating a source is
// what makes two output columns share a name, and the name a column ends up
// with is the one a pre-aggregation filter must be held against, so the
// builder and the filter path both name columns through this.
export function withUniqueColumnNames(columns) {
  const seenCounts = new Map();
  return columns.map((c) => {
    const base = c.column;
    const count = (seenCounts.get(base) ?? 0) + 1;
    seenCounts.set(base, count);
    if (count === 1) return c;
    return {
      ...c,
      column: `${base}_${count}`,
      sourceColumn: c.sourceColumn ?? c.internalName ?? base,
    };
  });
}

export class SpecBuilder {
  constructor(existingSpecification) {
    const spec = existingSpecification ?? {};
    this.sourceTables = spec.sourceTables ?? null;
    this.sourceColumns = spec.sourceColumns ?? null;
    this.joins = spec.joins ?? null;
    this.aggregate = spec.aggregate ?? null;
    this.group = spec.group ?? null;
    this.filters = spec.filters ?? null;
    this.preAggregationFilters = spec.preAggregationFilters ?? null;
    this.order = spec.order ?? null;
    this.outputColumns = spec.outputColumns ?? null;
    this.summaryRow = spec.summaryRow ?? null;
  }

  setSummaryRow(summaryRow) {
    this.summaryRow = summaryRow ?? null;
  }

  setAggregate(list) {
    this.aggregate = list?.length ? list : null;
  }

  setSourceTables(tables) {
    this.sourceTables = tables;
  }

  setGroupingFromParts(
    groupByColumns,
    columnAggregations,
    columnsData,
    extraAggregateColumns,
    includeDetails,
    includeTotal,
    allValuesEnumerations = new Set(),
    includeSubTotal = false
  ) {
    if (!groupByColumns || groupByColumns.size === 0) {
      this.group = null;
      return;
    }
    const extraColumnNames = new Set(
      extraAggregateColumns.map((e) => e.column)
    );
    const aggregate = (columnsData ?? [])
      .filter(
        (col) =>
          !isGroupingColumn(col.column) &&
          !groupByColumns.has(col.column) &&
          !extraColumnNames.has(col.column)
      )
      .map((col) => {
        const inputCol = inputColumnView(col);
        const func =
          columnAggregations[col.column] ??
          getDefaultAggregation(inputCol._type, inputCol._subType);
        return {
          column: col.column,
          expression: { func, sourceColumn: col.column },
        };
      });
    for (const extra of extraAggregateColumns) {
      aggregate.push({
        column: extra.column,
        expression: { func: extra.func, sourceColumn: extra.sourceColumn },
      });
    }
    this.setGrouping([
      {
        groupColumns: [...groupByColumns],
        aggregate,
        includeDetails: includeDetails || false,
        includeTotal: includeTotal || false,
        includeSubTotal: includeSubTotal || false,
        allValuesEnumerations: [...allValuesEnumerations],
      },
    ]);
  }

  setGrouping(groupObject) {
    this.group = groupObject?.length ? [{ ...groupObject[0] }] : null;
  }

  setFilters(filters) {
    this.filters = filters;
  }

  setPreAggregationFilters(filters) {
    this.preAggregationFilters = filters?.length ? filters : null;
  }

  setOrder(orderCriteria) {
    this.order = orderCriteria;
  }

  setOutputColumns(outputColumns) {
    this.outputColumns = outputColumns;
  }

  setJoins(joins) {
    this.joins = joins;
  }
  setTypedColumns(rawTypedColumnsList) {
    const hasGroup = (this.group ?? []).length > 0;

    const typedColumnsList = withUniqueColumnNames(
      rawTypedColumnsList.filter((c) => {
        if (!hasGroup && c.dataType === "groupAggregate") return false;
        return true;
      })
    );

    // outputColumns — all entries, stripped to permitted keys, but no duplicates
    this.outputColumns = typedColumnsList.map((c) => {
      const entry = { column: c.column };
      if (c.displayName !== undefined) entry.displayName = c.displayName;
      if (c.notForExport !== undefined) entry.notForExport = !!c.notForExport;
      if (c.pinned !== undefined) entry.pinned = !!c.pinned;
      if (c.decimals !== undefined) entry.decimals = c.decimals;
      return entry;
    });

    // sourceColumns — entries with dataType "source" or absent, that have a sourceTable.
    this.sourceColumns = typedColumnsList
      .filter((c) => (!c.dataType || c.dataType === "source") && c.sourceTable)
      .map((c) => ({
        column: c.column,
        sourceColumn: c.sourceColumn ?? c.internalName ?? c.column,
        sourceTable: c.sourceTable,
        displayName: c.displayName,
      }));

    // sourceTables — add any sourceTable values not already present.
    const tableSet = new Set(this.sourceTables ?? []);
    for (const c of typedColumnsList) {
      if (c.sourceTable) tableSet.add(c.sourceTable);
    }
    if (tableSet.size > (this.sourceTables ?? []).length) {
      this.sourceTables = Array.from(tableSet);
    }

    // aggregate — entries with dataType "aggregate" and an expression.
    this.aggregate = typedColumnsList
      .filter((c) => c.dataType === "aggregate" && c.expression !== undefined)
      .map((c) => {
        const entry = { column: c.column, expression: c.expression };
        if (c.displayName) entry.displayName = c.displayName;
        return entry;
      });
    if (this.aggregate.length === 0) this.aggregate = null;

    if (hasGroup) {
      // group aggregate — merge into group[0].aggregate by column key.
      const group = this.group[0];
      const groupAggregateMap = Object.fromEntries(
        group.aggregate
          .filter((c) => c.column && c.expression)
          .map((c) => [c.column, c])
      );
      const groupColumns = group.groupColumns;
      typedColumnsList.forEach((c) => {
        if (c.dataType === "groupAggregate") {
          const expression =
            c.expression ??
            (c.func
              ? { func: c.func, sourceColumn: c.sourceColumn }
              : undefined);
          groupAggregateMap[c.column] = {
            column: c.column,
            expression,
          };
        } else {
          if (
            !groupAggregateMap[c.column] &&
            !groupColumns.includes(c.column)
          ) {
            groupAggregateMap[c.column] = {
              column: c.column,
              expression: {
                sourceColumn: c.column,
                func: "CONCAT",
              },
            };
          }
        }
      });
      this.group[0].aggregate = Object.values(groupAggregateMap).filter(
        (ga) => !!ga.expression
      );
    } else {
      this.group = null;
    }
  }

  get() {
    return {
      sourceTables: this.sourceTables,
      sourceColumns: this.sourceColumns,
      joins: this.joins,
      aggregate: this.aggregate,
      group: this.group,
      filters: this.filters,
      preAggregationFilters: this.preAggregationFilters,
      order: this.order,
      outputColumns: this.outputColumns,
      summaryRow: this.summaryRow,
    };
  }
  build() {
    this.clean();
    return this.get();
  }
  // Runs validateAndRepair, logging the problem it reports.
  // Returns true when a problem was found, false when the specification is
  // clean.
  clean() {
    const problem = this.validateAndRepair();
    if (!problem) return false;
    logger.breadcrumb(`[SpecBuilder.clean] ${problem}`, {
      level: logger.LEVEL.WARNING,
    });
    return true;
  }

  // Validates and repairs the specification in-place.
  // Returns null on success or a message describing an unfixable problem.
  validateAndRepair() {
    const tableSet = new Set(this.sourceTables ?? []);

    // Remove sourceColumns whose sourceTable is not in sourceTables.
    if (this.sourceColumns) {
      this.sourceColumns = this.sourceColumns.filter(
        (sc) => sc.sourceTable && tableSet.has(sc.sourceTable)
      );
    }

    // Multi-table: there must be joins at all before their shape is judged.
    if (tableSet.size > 1 && (this.joins ?? []).length === 0) {
      return "Multiple source tables are selected but no joins are defined.";
    }

    // A join operand is either a source table or the joinName of an earlier
    // join; anything else is a stale reference and is reported, not repaired.
    // Joins are identified by joinName and reported by their display name,
    // which for a join created since the two were split is a uuid and a label.
    const allJoins = this.joins ?? [];
    if (allJoins.length > 0) {
      const joinLabels = new Map();
      for (const j of allJoins) {
        if (!j.joinName) continue;
        if (joinLabels.has(j.joinName)) {
          return `Two joins share the name "${joinDisplayNameOf(j)}".`;
        }
        joinLabels.set(j.joinName, joinDisplayNameOf(j));
      }

      const resolvableOperands = new Set(tableSet);
      for (const j of allJoins) {
        for (const operand of [j.leftTable, j.rightTable]) {
          if (!operand || resolvableOperands.has(operand)) continue;
          if (joinLabels.has(operand)) {
            return `Join "${joinDisplayNameOf(
              j
            )}" references join "${joinLabels.get(
              operand
            )}", which is defined after it.`;
          }
          return `Join "${joinDisplayNameOf(
            j
          )}" references unknown table "${operand}".`;
        }
        if (j.joinName) resolvableOperands.add(j.joinName);
      }
    }

    // Every source must fold into one result, not merely appear in some join.
    if (tableSet.size > 1 && !joinsFormSingleResult([...tableSet], allJoins)) {
      return "The selected sources are not joined into a single result.";
    }

    // Build the set of valid column names for subsequent checks.
    const sourceColumnNames = new Set(
      (this.sourceColumns ?? []).map((sc) => sc.column)
    );
    const aggregateColumnNames = new Set(
      (this.aggregate ?? []).map((a) => a.column)
    );
    const groupExtraNames =
      this.group?.length > 0
        ? this.group[0].aggregate
            .filter((g) => g?.expression?.sourceColumn !== g.column)
            .map((g) => g.column)
        : [];

    const validColumnNames = new Set([
      ...sourceColumnNames,
      ...aggregateColumnNames,
      ...groupExtraNames,
    ]);

    // Validate / repair group spec.
    if (this.group && this.group.length > 0) {
      const groupEntry = this.group[0];

      // groupColumns must be valid column names.
      const validGroupColumns = (groupEntry.groupColumns ?? []).filter((gc) =>
        validColumnNames.has(gc)
      );

      const groupColumnSet = new Set(validGroupColumns);

      const outputColumnSet = new Set();
      const outputColumnNames = [];
      this.outputColumns = this.outputColumns.filter((c) => {
        if (outputColumnSet.has(c.column)) return false;
        outputColumnSet.add(c.column);
        outputColumnNames.push(c.column);
        return true;
      });

      // aggregate entries in the group must cover every outputColumn not in groupColumns.
      const existingGroupAgg = groupEntry.aggregate ?? [];
      const existingGroupAggMap = new Map(
        existingGroupAgg.map((a) => [a.column, a])
      );

      const requiredAggColumns = outputColumnNames.filter(
        (col) => !groupColumnSet.has(col)
      );

      for (const col of requiredAggColumns) {
        if (!existingGroupAggMap.has(col)) {
          return `Output column "${col}" is neither in groupColumns nor has a group aggregate entry.`;
        }
        const entry = existingGroupAggMap.get(col);
        if (
          !entry.expression ||
          !entry.expression.func ||
          !entry.expression.sourceColumn
        ) {
          return `Group aggregate entry for column "${col}" is missing a valid expression (func and sourceColumn required).`;
        }
        const srcCol = entry.expression.sourceColumn;
        if (
          !sourceColumnNames.has(srcCol) &&
          !aggregateColumnNames.has(srcCol)
        ) {
          return `Group aggregate for column "${col}" references unknown sourceColumn "${srcCol}".`;
        }
      }

      // Any group aggregate columns not in outputColumns need an outputColumn entry added.
      for (const aggEntry of existingGroupAgg) {
        if (!outputColumnSet.has(aggEntry.column)) {
          this.outputColumns = this.outputColumns ?? [];
          this.outputColumns.push({ column: aggEntry.column });
        }
      }

      this.group = [
        {
          ...groupEntry,
          groupColumns: validGroupColumns,
          aggregate: existingGroupAgg,
        },
      ];

      // All outputColumns must be in groupColumns or group aggregate when grouping is active.
      const groupAggColumnSet = new Set(existingGroupAgg.map((a) => a.column));
      for (const oc of this.outputColumns ?? []) {
        if (
          !groupColumnSet.has(oc.column) &&
          !groupAggColumnSet.has(oc.column)
        ) {
          return `Output column "${oc.column}" is not covered by groupColumns or group aggregate.`;
        }
      }
    }

    // Validate filters.
    const filterValidColumns = new Set([
      ...validColumnNames,
      ...(this.group?.[0]?.aggregate ?? []).map((a) => a.column),
    ]);
    if (this.filters) {
      for (const f of this.filters) {
        if (!f.function) {
          return `A filter on column "${f.column}" is missing a function.`;
        }
        const cols = Array.isArray(f.column) ? f.column : [f.column];
        for (const col of cols) {
          if (!filterValidColumns.has(col)) {
            return `Filter references unknown column "${col}".`;
          }
        }
      }
    }

    return null;
  }
}
